import { DateTime } from 'luxon'

/**
 * Pure helpers behind the Admin area's navigation and its overview page.
 * No JSX and no React, so they're covered by `node:test` (adminNavModel.test.js).
 */

export const FUEL_LABELS = {
    electricity: 'Electricity',
    gas: 'Gas',
    water: 'Water'
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * Keep only the entries `canAccess` allows, and drop groups left empty — an
 * admin should never see a heading with nothing under it, or a link to a page
 * their org hasn't switched on.
 */
export function visibleAdminGroups(groups, canAccess) {
    return groups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => canAccess(item))
        }))
        .filter((group) => group.items.length > 0)
}

/**
 * Case-insensitive search across an entry's label, its description and its
 * group's title, so "invite" finds Users & roles and "tariff" finds Finance.
 * `resolve(item)` returns the displayed `{ label, description }` (terminology
 * applied). An empty query returns the groups untouched.
 */
export function searchAdminGroups(groups, query, resolve) {
    const needle = query.trim().toLowerCase()
    if (!needle) return groups
    return groups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => {
                const { label, description } = resolve(item)
                return [label, description, group.title]
                    .filter(Boolean)
                    .some((text) => text.toLowerCase().includes(needle))
            })
        }))
        .filter((group) => group.items.length > 0)
}

function isUnder(path, pathname) {
    return pathname === path || pathname.startsWith(`${path}/`)
}

/**
 * The admin entry a pathname belongs to — its own page or anything beneath it
 * (`/admin/alerts/rules/new` → Alert rules), plus any `activePaths` the route
 * declares for detail pages that live elsewhere (a meter's edit page sits
 * under `/admin/power/meters/…` but belongs to Meters). The longest match wins,
 * so a nested entry beats its parent.
 */
export function findActiveAdminItem(groups, pathname) {
    let best = null
    let bestLength = -1
    for (const group of groups) {
        for (const item of group.items) {
            for (const path of [item.path, ...(item.activePaths ?? [])]) {
                if (isUnder(path, pathname) && path.length > bestLength) {
                    best = item
                    bestLength = path.length
                }
            }
        }
    }
    return best
}

export function feedSeverity(entry) {
    if (
        !entry ||
        entry.status === null ||
        entry.status === 'failure' ||
        entry.stale
    ) {
        return 'error'
    }
    if (entry.status === 'partial') {
        return 'warning'
    }
    return 'success'
}

export function feedDescription(entry, now = DateTime.now()) {
    if (!entry || entry.status === null) {
        return 'No data fetched yet'
    }
    const when = entry.fetched_at
        ? DateTime.fromISO(entry.fetched_at).toRelative({ base: now })
        : null
    if (entry.stale) {
        return when ? `Stale: last ${when}` : 'Stale'
    }
    if (entry.status === 'failure') {
        return when ? `Failed ${when}` : 'Failed'
    }
    if (entry.status === 'partial') {
        return when ? `Partial ${when}` : 'Partial'
    }
    return when ? `Updated ${when}` : 'Up to date'
}

const SEVERITY_RANK = { error: 0, warning: 1, info: 2 }

/**
 * Everything on the admin summary worth an admin's attention, most severe
 * first. `error`/`warning` items are problems; `info` items are things to look
 * at that aren't wrong (open alerts), and don't stop the page reading healthy.
 * Each item links to the page that owns the thing it reports on.
 */
export function buildAttention(summary, now = DateTime.now()) {
    const items = []
    const fetchStatus = summary?.fetch_status || {}

    for (const [fuel, entry] of Object.entries(fetchStatus)) {
        const fuelLabel = FUEL_LABELS[fuel] ?? fuel
        const severity = feedSeverity(entry)
        if (severity !== 'success') {
            items.push({
                id: `feed-${fuel}`,
                severity,
                text: `${fuelLabel} readings: ${feedDescription(entry, now)}`,
                to: '/admin/fetch-status'
            })
        }
        // Exhausted gaps are days the system has stopped retrying — they will
        // never fill in on their own, so they're the gap count to act on.
        const exhausted = entry?.exhausted_gaps ?? 0
        if (exhausted > 0) {
            items.push({
                id: `gaps-${fuel}`,
                severity: 'warning',
                text: `${fuelLabel}: ${plural(exhausted, 'day')} of readings could not be recovered automatically`,
                to: '/admin/fetch-status'
            })
        }
    }

    const alerts = summary?.alerts || {}
    if (alerts.enabled) {
        if (alerts.paused) {
            items.push({
                id: 'alerts-paused',
                severity: 'warning',
                text: 'Alert emails are paused: nobody is being notified',
                to: '/admin/alerts/notifications'
            })
        }
        if (alerts.unassigned > 0) {
            items.push({
                id: 'alerts-unassigned',
                severity: 'warning',
                text: `${plural(alerts.unassigned, 'active alert')} with nobody assigned`,
                to: '/alerts'
            })
        }
        if (alerts.baseload_creep_active > 0) {
            items.push({
                id: 'alerts-baseload',
                severity: 'warning',
                text: `Baseload creep detected: ${plural(alerts.baseload_creep_active, 'active alert')}`,
                to: '/alerts'
            })
        }
        const otherActive = (alerts.active ?? 0) - (alerts.unassigned ?? 0)
        if (otherActive > 0) {
            items.push({
                id: 'alerts-active',
                severity: 'info',
                text: `${plural(otherActive, 'active alert')} being handled`,
                to: '/alerts'
            })
        }
    }

    if (summary?.isams_failures > 0) {
        items.push({
            id: 'mis-failures',
            severity: 'warning',
            text: `${summary.isams_failures} ${summary.isams_failures === 1 ? 'person' : 'people'} skipped by the directory import`,
            to: '/admin/isams-import-failures'
        })
    }

    // Stable sort: same-severity items keep the order above.
    return items.sort(
        (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    )
}

/** Problems only — `info` items don't make the page unhealthy. */
export function countProblems(attention) {
    return attention.filter((item) => item.severity !== 'info').length
}
